import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Plus, Search, UserPlus, Shield, Trash2, UserCog, Edit, AlertCircle, Users, ShieldCheck, Mail, Calendar, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatInCST } from '../utils/timezone';
import { Alert, AlertDescription } from '../components/ui/alert';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('staff'); // 'staff' or 'users'
  
  // Dialog states
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  
  // Form data for staff (admin/technician)
  const [staffFormData, setStaffFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'technician',
    is_active: true
  });
  
  // Form data for users (customer/partner)
  const [userFormData, setUserFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'customer',
    customer_id: '',
    is_active: true
  });
  
  const [editFormData, setEditFormData] = useState({
    email: '',
    full_name: '',
    role: '',
    customer_id: '',
    is_active: true,
    new_password: ''
  });

  // Separate staff and users
  const staffUsers = users.filter(u => u.role === 'admin' || u.role === 'technician');
  const portalUsers = users.filter(u => u.role === 'customer' || u.role === 'partner');

  useEffect(() => {
    loadUsers();
    loadCustomers();
  }, []);

  const loadUsers = async () => {
    try {
      // We need to add a users list endpoint to the backend
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      // If endpoint doesn't exist yet, we'll create it
      console.error('Users endpoint not available yet');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await api.get('/customers', { params: { limit: 10000, status: 'active' } });
      const custData = response.data;
      setCustomers(Array.isArray(custData) ? custData : custData.customers || []);
    } catch (error) {
      console.error('Failed to load customers');
    }
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/register', staffFormData);
      toast.success(`${staffFormData.role === 'admin' ? 'Admin' : 'Technician'} created successfully`);
      setStaffDialogOpen(false);
      setStaffFormData({
        email: '',
        password: '',
        full_name: '',
        role: 'technician',
        is_active: true
      });
      loadUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail;
      if (Array.isArray(errorMessage)) {
        toast.error(errorMessage.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMessage || 'Failed to create staff member');
      }
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = { ...userFormData };
      
      // Remove customer_id if not provided or role is partner
      if (!userFormData.customer_id || userFormData.role === 'partner') {
        delete dataToSubmit.customer_id;
      }
      
      await api.post('/auth/register', dataToSubmit);
      toast.success(`${userFormData.role === 'customer' ? 'Customer' : 'Partner'} user created successfully`);
      setUserDialogOpen(false);
      setUserFormData({
        email: '',
        password: '',
        full_name: '',
        role: 'customer',
        customer_id: '',
        is_active: true
      });
      loadUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail;
      if (Array.isArray(errorMessage)) {
        toast.error(errorMessage.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMessage || 'Failed to create user');
      }
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/users/${userId}`, { is_active: !currentStatus });
      toast.success('User status updated');
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setViewDialogOpen(true);
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      customer_id: user.customer_id || '',
      is_active: user.is_active,
      new_password: ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        email: editFormData.email,
        full_name: editFormData.full_name,
        role: editFormData.role,
        is_active: editFormData.is_active
      };

      // Only include customer_id for customer role users
      if (editFormData.role === 'customer' && editFormData.customer_id) {
        dataToSubmit.customer_id = editFormData.customer_id;
      } else if (editFormData.role !== 'customer') {
        dataToSubmit.customer_id = null;
      }

      // Include password if provided
      if (editFormData.new_password && editFormData.new_password.trim()) {
        dataToSubmit.password = editFormData.new_password;
      }

      await api.put(`/users/${editingUser.id}`, dataToSubmit);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail;
      if (Array.isArray(errorMessage)) {
        toast.error(errorMessage.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMessage || 'Failed to update user');
      }
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors',
      technician: 'bg-green-100 text-green-800 hover:bg-green-200 transition-colors',
      customer: 'bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors',
      partner: 'bg-orange-100 text-orange-800 hover:bg-orange-200 transition-colors'
    };
    return colors[role] || 'bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors';
  };

  const getRoleIcon = (role) => {
    const icons = {
      admin: Shield,
      technician: UserCog,
      customer: UserPlus,
      partner: UserPlus
    };
    const Icon = icons[role] || UserPlus;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">User Management</h1>
          <p className="text-gray-600 mt-1">Manage staff members and portal users</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="staff" className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Staff ({staffUsers.length})
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Portal Users ({portalUsers.length})
          </TabsTrigger>
        </TabsList>

        {/* STAFF TAB - Admins & Technicians */}
        <TabsContent value="staff" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Staff Members</CardTitle>
                  <CardDescription>Admins and technicians with full system access</CardDescription>
                </div>
                <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-staff-button">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Staff Member
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Add New Staff Member</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleStaffSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="staff_role">Role *</Label>
                        <Select
                          value={staffFormData.role}
                          onValueChange={(value) => setStaffFormData({ ...staffFormData, role: value })}
                        >
                          <SelectTrigger data-testid="staff-role-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">
                              <div className="flex items-center gap-2">
                                <Shield className="h-4 w-4" />
                                Admin (Full Access)
                              </div>
                            </SelectItem>
                            <SelectItem value="technician">
                              <div className="flex items-center gap-2">
                                <UserCog className="h-4 w-4" />
                                Technician
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="staff_full_name">Full Name *</Label>
                        <Input
                          id="staff_full_name"
                          value={staffFormData.full_name}
                          onChange={(e) => setStaffFormData({ ...staffFormData, full_name: e.target.value })}
                          required
                          placeholder="John Doe"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="staff_email">Email *</Label>
                        <Input
                          id="staff_email"
                          type="email"
                          value={staffFormData.email}
                          onChange={(e) => setStaffFormData({ ...staffFormData, email: e.target.value })}
                          required
                          placeholder="john@company.com"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="staff_password">Password *</Label>
                        <Input
                          id="staff_password"
                          type="password"
                          value={staffFormData.password}
                          onChange={(e) => setStaffFormData({ ...staffFormData, password: e.target.value })}
                          required
                          placeholder="Min 8 characters"
                        />
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={staffFormData.is_active}
                          onCheckedChange={(checked) => setStaffFormData({ ...staffFormData, is_active: checked })}
                        />
                        <Label className="cursor-pointer">Active (can login immediately)</Label>
                      </div>
                      
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setStaffDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit">
                          Create Staff Member
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <Search className="h-5 w-5 text-gray-400" />
                  <Input
                    placeholder="Search staff..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="max-w-sm"
                  />
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8">Loading staff members...</div>
              ) : staffUsers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ShieldCheck className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-semibold mb-2">No Staff Members</p>
                  <p className="text-sm">Add your first staff member to get started</p>
                </div>
              ) : (
                <>
                {/* Mobile Card View - Staff */}
                <div className="md:hidden space-y-2 p-2" data-testid="staff-mobile-cards">
                  {staffUsers
                    .filter(user => !searchTerm || user.email.toLowerCase().includes(searchTerm.toLowerCase()) || user.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((user) => (
                      <div key={user.id} className="border rounded-lg p-3 bg-white">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-semibold shrink-0 text-sm">
                              {user.full_name?.[0] || user.email?.[0] || 'U'}
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-sm">{user.full_name}</div>
                              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Badge className={user.role === 'admin' ? 'bg-purple-100 text-purple-800 text-xs' : 'bg-blue-100 text-blue-800 text-xs'}>
                              {user.role === 'admin' ? 'Admin' : 'Tech'}
                            </Badge>
                            <Badge className={user.is_active ? 'bg-green-100 text-green-800 text-xs' : 'bg-gray-100 text-gray-800 text-xs'}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex justify-end gap-1 mt-2 pt-2 border-t">
                          <Button size="sm" variant="ghost" onClick={() => handleViewUser(user)}><Eye className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => handleEditClick(user)}><Edit className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(user.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    ))}
                </div>
                {/* Desktop Table - Staff */}
                <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staffUsers
                      .filter(user => 
                        !searchTerm || 
                        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        user.full_name.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-semibold">
                                {user.full_name?.[0] || user.email?.[0] || 'U'}
                              </div>
                              <span className="font-semibold">{user.full_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-gray-600">
                              <Mail className="h-4 w-4" />
                              {user.email}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                              {user.role === 'admin' ? (
                                <><Shield className="h-3 w-3 mr-1 inline" />Admin</>
                              ) : (
                                <><UserCog className="h-3 w-3 mr-1 inline" />Technician</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={user.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatInCST(user.created_at, 'MMM d, yyyy')}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleViewUser(user)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditClick(user)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(user.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PORTAL USERS TAB - Customers & Partners */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Portal Users</CardTitle>
                  <CardDescription>Customer and partner users with limited portal access</CardDescription>
                </div>
                <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-user-button">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Portal User
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Add New Portal User</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUserSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="user_role">User Type *</Label>
                        <Select
                          value={userFormData.role}
                          onValueChange={(value) => setUserFormData({ ...userFormData, role: value })}
                        >
                          <SelectTrigger data-testid="user-role-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="customer">
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                Customer (Portal Access)
                              </div>
                            </SelectItem>
                            <SelectItem value="partner">
                              <div className="flex items-center gap-2">
                                <UserPlus className="h-4 w-4" />
                                Partner
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="user_full_name">Full Name *</Label>
                        <Input
                          id="user_full_name"
                          value={userFormData.full_name}
                          onChange={(e) => setUserFormData({ ...userFormData, full_name: e.target.value })}
                          required
                          placeholder="Jane Smith"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="user_email">Email *</Label>
                        <Input
                          id="user_email"
                          type="email"
                          value={userFormData.email}
                          onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                          required
                          placeholder="jane@customer.com"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="user_password">Password *</Label>
                        <Input
                          id="user_password"
                          type="password"
                          value={userFormData.password}
                          onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                          required
                          placeholder="Min 8 characters"
                        />
                      </div>
                      
                      {userFormData.role === 'customer' && (
                        <div className="space-y-2">
                          <Label htmlFor="user_customer_id">Link to Customer Account</Label>
                          <Select
                            value={userFormData.customer_id || undefined}
                            onValueChange={(value) => setUserFormData({ ...userFormData, customer_id: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a customer" />
                            </SelectTrigger>
                            <SelectContent>
                              {customers.map((customer) => (
                                <SelectItem key={customer.id} value={customer.id}>
                                  {customer.company_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500">Optional: Link this user to a customer account for ticket access</p>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={userFormData.is_active}
                          onCheckedChange={(checked) => setUserFormData({ ...userFormData, is_active: checked })}
                        />
                        <Label className="cursor-pointer">Active (can login immediately)</Label>
                      </div>
                      
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit">
                          Create Portal User
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <Search className="h-5 w-5 text-gray-400" />
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="max-w-sm"
                  />
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8">Loading portal users...</div>
              ) : portalUsers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-semibold mb-2">No Portal Users</p>
                  <p className="text-sm">Add customer or partner users for portal access</p>
                </div>
              ) : (
                <>
                {/* Mobile Card View - Portal Users */}
                <div className="md:hidden space-y-2 p-2" data-testid="portal-mobile-cards">
                  {portalUsers
                    .filter(user => !searchTerm || user.email.toLowerCase().includes(searchTerm.toLowerCase()) || user.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((user) => {
                      const linkedCustomer = user.customer_id ? customers.find(c => c.id === user.customer_id) : null;
                      return (
                        <div key={user.id} className="border rounded-lg p-3 bg-white">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center text-white font-semibold shrink-0 text-sm">
                                {user.full_name?.[0] || user.email?.[0] || 'U'}
                              </div>
                              <div className="min-w-0">
                                <div className="font-semibold text-sm">{user.full_name}</div>
                                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                                {linkedCustomer && <div className="text-xs text-gray-500 mt-0.5">{linkedCustomer.company_name}</div>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Badge className={user.role === 'customer' ? 'bg-green-100 text-green-800 text-xs' : 'bg-orange-100 text-orange-800 text-xs'}>
                                {user.role === 'customer' ? 'Customer' : 'Partner'}
                              </Badge>
                              <Badge className={user.is_active ? 'bg-green-100 text-green-800 text-xs' : 'bg-gray-100 text-gray-800 text-xs'}>
                                {user.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex justify-end gap-1 mt-2 pt-2 border-t">
                            <Button size="sm" variant="ghost" onClick={() => handleViewUser(user)}><Eye className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => handleEditClick(user)}><Edit className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(user.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      );
                    })}
                </div>
                {/* Desktop Table - Portal Users */}
                <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Linked Customer</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {portalUsers
                      .filter(user => 
                        !searchTerm || 
                        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        user.full_name.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((user) => {
                        const linkedCustomer = user.customer_id ? customers.find(c => c.id === user.customer_id) : null;
                        return (
                          <TableRow key={user.id}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center text-white font-semibold">
                                  {user.full_name?.[0] || user.email?.[0] || 'U'}
                                </div>
                                <span className="font-semibold">{user.full_name}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2 text-gray-600">
                                <Mail className="h-4 w-4" />
                                {user.email}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={user.role === 'customer' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                                {user.role === 'customer' ? (
                                  <><Users className="h-3 w-3 mr-1 inline" />Customer</>
                                ) : (
                                  <><UserPlus className="h-3 w-3 mr-1 inline" />Partner</>
                                )}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {linkedCustomer ? linkedCustomer.company_name : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge className={user.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                {user.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatInCST(user.created_at, 'MMM d, yyyy')}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleViewUser(user)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEditClick(user)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDelete(user.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
                </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={editFormData.full_name}
                onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>New Password (leave blank to keep current)</Label>
              <Input
                type="password"
                value={editFormData.new_password}
                onChange={(e) => setEditFormData({ ...editFormData, new_password: e.target.value })}
                placeholder="Enter new password or leave blank"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={editFormData.is_active}
                onCheckedChange={(checked) => setEditFormData({ ...editFormData, is_active: checked })}
              />
              <Label>Active</Label>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View User Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white text-2xl font-semibold">
                  {selectedUser.full_name?.[0] || selectedUser.email?.[0] || 'U'}
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{selectedUser.full_name}</h3>
                  <p className="text-gray-600">{selectedUser.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Role</p>
                  <Badge className={
                    selectedUser.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                    selectedUser.role === 'technician' ? 'bg-blue-100 text-blue-800' :
                    selectedUser.role === 'customer' ? 'bg-green-100 text-green-800' :
                    'bg-orange-100 text-orange-800'
                  }>
                    {selectedUser.role}
                  </Badge>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500 mb-1">Status</p>
                  <Badge className={selectedUser.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                    {selectedUser.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500 mb-1">Created</p>
                  <p className="text-sm font-medium">{formatInCST(selectedUser.created_at, 'MMM d, yyyy')}</p>
                </div>
                
                {selectedUser.customer_id && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Linked Customer</p>
                    <p className="text-sm font-medium">
                      {customers.find(c => c.id === selectedUser.customer_id)?.company_name || 'Unknown'}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => {
                  setViewDialogOpen(false);
                  handleEditClick(selectedUser);
                }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button onClick={() => setViewDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
